const MINUTE = 60000;

export function formatMinutes(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) return `${minutes} min`;
  const hourText = `${hours} ${hours === 1 ? "timme" : "timmar"}`;
  return minutes ? `${hourText} ${minutes} min` : hourText;
}

function dateAt(date, time) {
  const day = typeof date === "string" ? date.slice(0, 10) : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !/^\d{2}:\d{2}$/.test(time || "")) return null;
  const result = new Date(`${day}T${time}:00`);
  return Number.isNaN(result.getTime()) ? null : result;
}

export function scheduledRows(shifts, employees = []) {
  const identities = new Map();
  employees.forEach((employee) => {
    [employee.id, employee.employeeId, employee.authUid, employee.uid, employee.email].filter(Boolean)
      .forEach((id) => identities.set(id, employee.id));
  });
  const canonical = (id) => identities.get(id) || id;
  return shifts.flatMap((shift) => {
    const [legacyStart, legacyEnd] = (shift.time || "").split(/\s*[-–]\s*/);
    const start = dateAt(shift.date, shift.startTime || legacyStart);
    const end = dateAt(shift.date, shift.endTime || legacyEnd);
    if (!start || !end) return [];
    if (end <= start) end.setDate(end.getDate() + 1);
    const rows = shift.employees?.length ? shift.employees : [shift];
    return rows.filter((row) => row.employeeId && !row.isObemannad).map((row, index) => {
      const breakMs = (row.breaks || []).reduce((sum, pause) => {
        const from = dateAt(shift.date, pause.startTime);
        const to = dateAt(shift.date, pause.endTime);
        if (!from || !to) return sum;
        if (from < start && end.getDate() !== start.getDate()) from.setDate(from.getDate() + 1);
        if (to <= from) to.setDate(to.getDate() + 1);
        return sum + Math.max(0, Math.min(+end, +to) - Math.max(+start, +from));
      }, 0);
      const employeeId = canonical(row.employeeId);
      const employee = employees.find((item) => item.id === employeeId);
      return {
        id: `schedule-${shift.id}-${index}`, employeeId,
        shiftId: shift.id, assignedEmployeeId: row.employeeId, payrollTime: row.payrollTime || null,
        message: typeof shift.message === "string" ? shift.message.trim() : "",
        employeeMessage: row !== shift && typeof row.message === "string" ? row.message.trim() : "",
        aliases: [employeeId, row.employeeId, employee?.authUid, employee?.uid, employee?.email].filter(Boolean),
        employeeName: row.name || employee?.name || shift.employee || "Okänd medarbetare",
        start, end, breakMs: Math.min(breakMs, end - start),
        plannedMs: Math.max(0, end - start - breakMs),
      };
    });
  });
}

export function compareSessions(sessions, schedules, now = new Date()) {
  const used = new Set();
  const result = sessions.map((session) => {
    const anchor = session.clockIn || session.clockOut;
    if (!anchor) return session;
    const candidates = schedules.filter((shift) => {
      if (!shift.aliases.includes(session.employeeId)) return false;
      // Match only overlapping work, or a punch within two hours of the shift.
      const from = session.clockIn || anchor;
      const to = session.clockOut || anchor;
      return (from <= shift.end && to >= shift.start) ||
        Math.abs(anchor - (session.clockIn ? shift.start : shift.end)) <= 120 * MINUTE;
    }).sort((a, b) => Math.abs(anchor - (session.clockIn ? a.start : a.end)) -
      Math.abs(anchor - (session.clockIn ? b.start : b.end)));
    if (!candidates.length) return session;
    const schedule = candidates[0];
    used.add(schedule.id);
    return { ...session, schedule };
  });
  // Several punch intervals may belong to one shift; compare the total once.
  schedules.forEach((schedule) => {
    const matches = result.filter((row) => row.schedule?.id === schedule.id);
    if (matches.length) {
      const total = matches.reduce((sum, row) => sum + (row.duration || 0), 0);
      const arrivals = matches.filter((row) => row.clockIn).map((row) => +row.clockIn);
      const departures = matches.filter((row) => row.clockOut).map((row) => +row.clockOut);
      matches.forEach((row) => {
        row.shiftWorkedMs = matches.every((item) => item.clockIn && item.clockOut) ? total : null;
        row.firstArrival = +row.clockIn === Math.min(...arrivals);
        row.lastDeparture = +row.clockOut === Math.max(...departures);
      });
    } else if (!used.has(schedule.id)) {
      result.push({ id: schedule.id, employeeId: schedule.employeeId,
        employeeName: schedule.employeeName, date: schedule.start,
        eventIds: [], clockIn: null, clockOut: null, schedule,
        missing: schedule.start <= now, planned: schedule.start > now });
    }
  });
  return result.sort((a, b) => (b.date || b.clockOut) - (a.date || a.clockOut));
}

export function timingNotes(session, now = new Date()) {
  const { schedule, clockIn, clockOut } = session;
  if (!schedule) return "Inget matchande schemapass";
  if (session.planned) return "Schemalagt pass har inte börjat";
  if (session.missing) return "Ingen instämpling registrerad";
  const notes = [];
  if (clockIn && session.firstArrival !== false) {
    const minutes = Math.round((clockIn - schedule.start) / MINUTE);
    notes.push(minutes > 0 ? `${formatMinutes(minutes)} sen` : minutes < 0 ? `${formatMinutes(-minutes)} tidig` : "In i tid");
  }
  if (clockOut && session.lastDeparture !== false) {
    const minutes = Math.round((clockOut - schedule.end) / MINUTE);
    notes.push(minutes > 0 ? `${formatMinutes(minutes)} efter sluttid` : minutes < 0 ? `${formatMinutes(-minutes)} tidigt` : "Ut i tid");
  } else if (clockIn && !clockOut && now - schedule.end > 60 * MINUTE) {
    notes.push("Möjligen glömd utstämpling");
  }
  return notes.join(" · ") || "Del av schemapasset";
}
